//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import { SubsectionText } from '@components/atoms/text/Text';

export interface CollapsibleSectionProps {
  className?: string;
  children: ReactNode;
  isCollapsed?: boolean;
  leftComponent?: ReactNode;
  rightComponent?: ReactNode;
  title: string;
}

export const CollapsibleSection = ({
  children,
  className,
  isCollapsed,
  leftComponent,
  rightComponent,
  title,
}: CollapsibleSectionProps) => {
  const [isSectionCollapsed, setIsSectionCollapsed] = useState(
    isCollapsed ?? false,
  );

  return (
    <Details className={className} open={!isCollapsed}>
      <Summary
        onClick={() => setIsSectionCollapsed((isCollapsed) => !isCollapsed)}
      >
        <Title>
          {leftComponent}
          <SubsectionText>{title}</SubsectionText>
          {rightComponent}
        </Title>
        <ToggleIcon isCollapsed={isSectionCollapsed} name='chevron-down' />
      </Summary>
      <Content>{children}</Content>
    </Details>
  );
};

const Details = styled.details(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[3]};
  `,
);

const Summary = styled.summary(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spacings[2]};
    cursor: pointer;
    outline: none;
    
    &::-webkit-details-marker {
      display: none; /* Hide the default arrow on safari */
    }
  `,
);

const Title = styled.span(
  ({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacings[2]};
`,
);

const ToggleIcon = styled(Icon)<{ isCollapsed: boolean }>(
  ({ isCollapsed }) => `
    transform: rotate(${isCollapsed ? '180deg' : 0});
    transition: transform 200ms ease;
  `,
);

const Content = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[2]} ${theme.spacings[0]};
  `,
);
